
import React from "react";
import { format, isSameDay } from "date-fns";
import { Edit, Trash2, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useLanguage } from "@/components/LanguageContext";

interface CalendarEventProps {
  event: any;
  weekDays: Date[];
  settings: any;
  getEventStatusColor: (event: any) => string;
  onEventClick: (event: any) => void;
  onEventApprove: (event: any) => void;
  onEventEdit: (event: any) => void;
  onEventDelete: (event: any) => void;
}

export const CalendarEvent: React.FC<CalendarEventProps> = ({
  event,
  weekDays,
  settings,
  getEventStatusColor,
  onEventClick,
  onEventApprove,
  onEventEdit,
  onEventDelete
}) => {
  const { t, language } = useLanguage();
  
  const startTime = new Date(event.start_time);
  const endTime = new Date(event.end_time);
  const dayIndex = weekDays.findIndex(day => isSameDay(day, startTime));
  
  if (dayIndex === -1) return null;
  
  const workStartHour = parseInt(settings.work_start_time.split(':')[0]);
  const startHour = startTime.getHours();
  const startMinutes = startTime.getMinutes();
  const duration = (endTime.getTime() - startTime.getTime()) / (1000 * 60);

  const minutesSinceStart = (startHour - workStartHour) * 60 + startMinutes;
  const top = minutesSinceStart + 80;
  const height = duration;

  const leftOffset = 60;
  const columnWidth = `calc((100% - ${leftOffset}px) / ${weekDays.length})`;

  const studentName = event.students?.first_name && event.students?.last_name 
    ? `${event.students.first_name} ${event.students.last_name}`
    : null;
  
  const displayTitle = event.event_type === 'other' 
    ? event.title 
    : (studentName || (language === 'en' ? "Individual Training" : "Treino Individual"));

  return (
    <div 
      key={event.id} 
      className={`absolute p-2 rounded-lg shadow-sm backdrop-blur-sm transition-all duration-200 hover:scale-[1.02] hover:shadow-md ${getEventStatusColor(event)}`} 
      style={{
        top: `${top}px`,
        left: `calc(${leftOffset}px + (${dayIndex} * ${columnWidth}))`,
        width: `calc(${columnWidth} - 10px)`,
        height: `${height}px`,
        minHeight: "20px"
      }}
      onClick={() => onEventClick(event)}
    >
      <div className="flex justify-between items-start gap-2">
        <div className="flex-1 min-w-0">
          <div className="font-medium text-sm truncate">
            {displayTitle}
          </div>
          <div className="text-xs">
            {format(startTime, "HH:mm")}
          </div>
          {event.event_type !== 'other' && studentName && event.title && event.title !== "Treino Individual" && event.title !== "Individual Training" && (
            <div className="text-xs text-muted-foreground dark:text-gray-300 truncate">
              {event.title}
            </div>
          )}
          {event.status === 'pending' && (
            <div className="text-red-700 dark:text-red-400 text-xs font-medium mt-1">
              {t("dashboard.schedule.eventDialog.awaitingApproval")}
            </div>
          )}
          {event.status === 'approved' && (
            <div className="text-green-700 dark:text-green-400 text-xs font-medium mt-1">
              {language === 'en' ? 'Approved' : 'Aprovado'}
            </div>
          )}
        </div>
        <div className="flex gap-1">
          {event.status === 'pending' && (
            <Button 
              variant="ghost" 
              size="sm" 
              className="h-6 w-6 p-0 text-green-600 hover:text-green-700 hover:bg-green-50 dark:text-green-400 dark:hover:text-green-300 dark:hover:bg-green-900/30"
              onClick={e => {
                e.stopPropagation();
                onEventApprove(event);
              }}
            >
              <Check className="h-3 w-3" />
            </Button>
          )}
          <Button 
            variant="ghost" 
            size="sm" 
            className="h-6 w-6 p-0 dark:text-gray-300 dark:hover:text-white"
            onClick={e => {
              e.stopPropagation();
              onEventEdit(event);
            }}
          >
            <Edit className="h-3 w-3" />
          </Button>
          <Button 
            variant="ghost" 
            size="sm" 
            className="h-6 w-6 p-0 dark:text-gray-300 dark:hover:text-white"
            onClick={e => {
              e.stopPropagation();
              onEventDelete(event);
            }}
          >
            <Trash2 className="h-3 w-3" />
          </Button>
        </div>
      </div>
    </div>
  );
};
